import React from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import type { SharedProps } from '../../types';
import type { NewToken } from '../../types/developers';
import CopyBlock from '../worklog/CopyBlock';
import { button, inlineCode, primaryButton } from './ui';

type Props = {
  // Where to come back to after generating (return_to).
  topic: string;
  // Goes into the token's name, so the settings list shows where it came from.
  clientLabel: string;
  newToken: NewToken | null;
};

// "MM-DD" in Asia/Shanghai, the app's time zone.
const today = () =>
  new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', timeZone: 'Asia/Shanghai' })
    .format(new Date())
    .replace('/', '-');

// Step 1 on a client page: signed out, signed in without a token yet, or just
// generated. Generating posts to the settings endpoint with return_to and comes
// back here with the plaintext in flash — once. Reload or leave and it is gone,
// the same rule as the settings page.
const TokenStep: React.FC<Props> = ({ topic, clientLabel, newToken }) => {
  const { props } = usePage<SharedProps & { errors?: Record<string, string> }>();
  const [busy, setBusy] = React.useState(false);

  if (!props.auth.user) {
    return (
      <>
        <p className="text-sm text-gray-700">
          接入要用一个 <code className={inlineCode}>pt_</code> 开头的 token，它等同你的账号权限。先登录，再在这里生成一个。
        </p>
        <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
          <Link href="/login" className={primaryButton}>
            登录
          </Link>
          <Link href="/register" className={button}>
            注册
          </Link>
        </div>
      </>
    );
  }

  if (newToken) {
    return (
      <>
        <p className="mb-1.5 text-sm font-semibold text-green-700">✓ 已生成</p>
        <CopyBlock code={newToken.token} />
        <p className="mt-1.5 text-xs text-gray-500">
          只显示这一次，离开这页就看不到了。<strong>下面的提示词和命令已经替你填好它。</strong>
        </p>
      </>
    );
  }

  const generate = () => {
    setBusy(true);
    router.post(
      '/settings/api_tokens',
      { name: `接入页 · ${clientLabel} · ${today()}`, return_to: topic },
      { preserveScroll: true, onFinish: () => setBusy(false) },
    );
  };

  return (
    <>
      <p className="text-sm text-gray-700">
        token 等同你的账号权限，<strong>只在生成那一刻完整显示一次</strong>。
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-3">
        <button type="button" className={primaryButton} disabled={busy} onClick={generate}>
          {busy ? '生成中…' : '生成 token'}
        </button>
        <Link href="/settings" className="text-sm text-gray-500 hover:text-blue-600">
          去设置页管理已有的 token
        </Link>
      </div>
      {props.errors?.name && <p className="mt-2 text-sm text-red-600">{props.errors.name}</p>}
    </>
  );
};

export default TokenStep;
